import debug from 'debug';
import { components } from '../connections/integro-api-types';
import Big from 'big.js';
import { isString, isObject, extend, find, get } from 'lodash';

const log = debug('itp:integro.helpers.InvoiceLineHelper');

type Currency = components['schemas']['Currency'];
type TaxCode = components['schemas']['TaxCode'];
type ReadInvoice = components['schemas']['ReadInvoice'];
type ReadInvoiceLine = components['schemas']['ReadInvoiceLine'];
type ProductBrief = components['schemas']['ProductBrief'];
type ReadContractService = components['schemas']['ReadContractService'];
type ReadContractServiceFee = components['schemas']['ReadContractServiceFee'];
type ItemExchangeRate = components['schemas']['ItemExchangeRate-Output'];

interface InvoiceLineEditor extends ReadInvoiceLine {}

interface DigitSettings {
  default: number;
  quantity: number;
  unitPrice: number;
  amounts: number;
}

export class CurrencyDigitsHelper {
  currency: Currency;
  digits: DigitSettings;

  constructor(currency: Currency) {
    this.update(currency);
  }

  update(currency: Currency) {
    this.currency = currency;
    this.digits = {
      default: currency.digits,
      quantity: currency.quantity_digits,
      unitPrice: currency.price_digits,
      amounts: currency.digits,
    };
  }
}

export class InvoiceLineCalculator {
  private _recalculating: boolean = false;

  private readonly defaultTaxCode: string;
  private readonly defaultDiscount: number;
  private readonly currency: Currency;
  private readonly taxCodes: TaxCode[];
  digits: DigitSettings;

  constructor(currency: Currency, taxCodes: TaxCode[]) {
    this.currency = currency;
    this.taxCodes = taxCodes;
    this.digits = new CurrencyDigitsHelper(currency).digits;
  }

  updateUnitPrice(
    item: InvoiceLineEditor,
    decimals: number,
    exchange_rate?: ItemExchangeRate,
  ) {
    /**
     * updateUnitPrice recalculates the unit price from the base price.
     */

    if (!item.base_price) {
      return;
    }

    if (item.base_price.currency == this.currency.code) {
      item.price = Big(item.base_price.amount).round(decimals).toNumber();
      return;
    }

    if (!exchange_rate) {
      return;
    }

    item.price = Big(item.base_price.amount)
      .mul(exchange_rate.value)
      .round(decimals)
      .toNumber();
  }

  calculateUnitPrice(item: InvoiceLineEditor, tax: TaxCode, decimals: number) {
    /**
     * Calculate unit price.
     *
     * formula: unit_price = net_amount / quantity / (1 - discount/100)
     */
    if (!item.net_amount) {
      return;
    }

    if (!item.net_amount) {
      return;
    }

    const netAmount = Big(item.net_amount);
    const quantity = Big(item.quantity);
    const discount = Big(item.discount);

    item.price = netAmount
      .div(quantity)
      .div(Big(1).minus(discount.div(100)))
      .round(decimals)
      .toNumber();
  }

  calculateNetAmount(item: InvoiceLineEditor, tax: TaxCode, decimals: number) {
    /**
     * Calculate net amount.
     *
     * formula: net_amount = quantity * price * (1 - discount/100)
     */
    const quantity = Big(item.quantity || 0);
    const price = Big(item.price || 0);
    const discount = Big(item.discount || 0);

    item.net_amount = quantity
      .mul(price)
      .mul(Big(1).minus(discount.div(100)))
      .round(decimals)
      .toNumber();
  }

  calculateNetAmountFromAmount(
    item: InvoiceLineEditor,
    tax: any,
    decimals: number,
  ) {
    /**
     * Calculate net amount from gross amount.
     *
     * formula: net_amount = amount / (1 + tax_rate/100)
     */
    const amount = Big(item.amount);
    const taxFactor = Big(tax.rate).div(100).plus(1);

    item.net_amount = amount.div(taxFactor).round(decimals).toNumber();
  }

  calculateAmount(item: InvoiceLineEditor, tax: TaxCode, decimals: number) {
    /**
     * Calculate gross amount.
     *
     * formula: amount = net_amount * (1 + tax_rate/100)
     */
    if (item.net_amount === 0) {
      item.amount = 0;
      return;
    }

    if (item.net_amount == null || item.tax_code == null) {
      return;
    }

    const netAmount = Big(item.net_amount);
    item.amount = Big(item.net_amount)
      .mul(Big(tax.rate).div(100).plus(1))
      .round(decimals)
      .toNumber();
  }

  public recalculateLineItem(item: InvoiceLineEditor, field: string) {
    /**
     * Recalculate values when a field changed.
     *
     * @param item - The target line item
     * @param field - The name of the changed field.
     */
    if (this._recalculating) {
      return false;
    }

    try {
      this._recalculating = true;

      log('recalculateLineItem[%s] %o', field, item);

      const currency = this.currency;
      const taxCode = find(this.taxCodes, { id: item.tax_code });

      if (item.quantity == 0) {
        return;
      }

      switch (field) {
        case 'base_price': {
          const exchange_rate: ItemExchangeRate = get(
            item,
            'exchange_rate',
            null,
          );
          this.updateUnitPrice(item, currency.price_digits, exchange_rate);
          this.calculateNetAmount(item, taxCode, currency.digits);
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'quantity':
        case 'price':
        case 'discount': {
          this.calculateNetAmount(item, taxCode, currency.digits);
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'net_amount': {
          this.calculateUnitPrice(item, taxCode, currency.price_digits);
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'tax_code': {
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'amount': {
          this.calculateNetAmountFromAmount(item, taxCode, currency.digits);
          this.calculateUnitPrice(item, taxCode, currency.price_digits);
          break;
        }
      }
    } finally {
      this._recalculating = false;
    }
  }

  public applyContractServiceFee(
    item: InvoiceLineEditor,
    service: ReadContractService,
    fee: ReadContractServiceFee,
  ) {
    item.price = fee.amount;
    item.quantity = fee.quantity || 1;
    item.unit = fee.unit;
    this.recalculateLineItem(item, 'quantity');
  }
}

export class InvoiceLineHelper {
  /**
   * Helper class for calculations and line value settings.
   */
  private _recalculating: boolean = false;

  private readonly invoice: ReadInvoice;
  private readonly defaultTaxCode: string;
  private readonly defaultDiscount: number;
  private readonly currencies: Currency[];
  private readonly taxCodes: TaxCode[];

  constructor(
    invoice: ReadInvoice,
    currencies: Currency[],
    taxCodes: TaxCode[],
  ) {
    this.invoice = invoice;
    this.defaultTaxCode = this.invoice.meta!['default_tax_code'];
    this.defaultDiscount = this.invoice.meta!['default_discount'];
    this.currencies = currencies;
    this.taxCodes = taxCodes;
  }

  get isIncomingInvoice() {
    return this.invoice.invoice_kind == 'incoming';
  }

  get currency() {
    const invoice_currency = this.invoice.currency;
    for (const currency of this.currencies) {
      if (currency.code == invoice_currency) {
        return currency;
      }
    }
    throw new Error(`Currency not found: ${invoice_currency}`);
  }

  getNearestLine(item: InvoiceLineEditor): ReadInvoiceLine | null {
    const index =
      item.order_index == null
        ? this.invoice.lines.data.length - 1
        : item.order_index == 0
          ? 1
          : item.order_index - 1;

    for (const line of this.invoice.lines.data) {
      if (line.order_index == index) {
        return line as any as ReadInvoiceLine;
      }
    }
  }

  public onProductSelected(
    item: InvoiceLineEditor,
    product?: ProductBrief | string,
  ) {
    const overrides = {};

    if (!product) {
      product = item.product;
    }

    if (isObject(product) && product.id == null) {
      product = product.name;
      overrides['product'] = product;
    }

    if (isString(product)) {
      item.description = product;
      item.tax_code = this.defaultTaxCode;

      const nearestRow = this.getNearestLine(item);
      if (nearestRow) {
        item.unit = nearestRow.unit;
        item.discount = nearestRow.discount;
      }
    } else {
      item.product = product;
      item.unit = product.unit;
      item.description = product.name;
      item.tax_code =
        this.defaultTaxCode ||
        (this.invoice.invoice_kind === 'outgoing'
          ? product.sales_vat_code
          : product.purchase_vat_code);
      item.intermediated_service = product.intermediated_service || false;
    }

    if (overrides) {
      extend(item, overrides);
    }
  }

  calculateUnitPrice(item: InvoiceLineEditor, tax: TaxCode, decimals: number) {
    /**
     * Calculate unit price.
     *
     * formula: unit_price = net_amount / quantity / (1 - discount/100)
     */
    const netAmount = Big(item.net_amount);
    const quantity = Big(item.quantity);
    const discount = Big(item.discount);

    item.price = netAmount
      .div(quantity)
      .div(Big(1).minus(discount.div(100)))
      .round(decimals)
      .toNumber();
  }

  calculateNetAmount(item: InvoiceLineEditor, tax: TaxCode, decimals: number) {
    /**
     * Calculate net amount.
     *
     * formula: net_amount = quantity * price * (1 - discount/100)
     */
    const quantity = Big(item.quantity);
    const price = Big(item.price);
    const discount = Big(item.discount);

    item.net_amount = quantity
      .mul(price)
      .mul(Big(1).minus(discount.div(100)))
      .round(decimals)
      .toNumber();
  }

  calculateNetAmountFromAmount(
    item: InvoiceLineEditor,
    tax: any,
    decimals: number,
  ) {
    /**
     * Calculate net amount from gross amount.
     *
     * formula: net_amount = amount / (1 + tax_rate/100)
     */
    const amount = Big(item.amount);
    const taxFactor = Big(tax.rate).div(100).plus(1);

    item.net_amount = amount.div(taxFactor).round(decimals).toNumber();
  }

  calculateAmount(item: InvoiceLineEditor, tax: TaxCode, decimals: number) {
    /**
     * Calculate gross amount.
     *
     * formula: amount = net_amount * (1 + tax_rate/100)
     */
    item.amount = Big(item.net_amount)
      .mul(Big(tax.rate).div(100).plus(1))
      .round(decimals)
      .toNumber();
  }

  public recalculateLineItem(item: InvoiceLineEditor, field: string) {
    /**
     * Recalculate values when a field changed.
     *
     * @param item - The target line item
     * @param field - The name of the changed field.
     */

    if (this._recalculating) {
      return false;
    }

    try {
      this._recalculating = true;

      const currency = this.currency;
      const taxCode = find(this.taxCodes, { id: item.tax_code });

      if (item.quantity == 0) {
        return;
      }

      switch (field) {
        case 'quantity':
        case 'price':
        case 'discount': {
          this.calculateNetAmount(item, taxCode, currency.digits);
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'net_amount': {
          this.calculateUnitPrice(item, taxCode, currency.price_digits);
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'tax_code': {
          this.calculateAmount(item, taxCode, currency.digits);
          break;
        }

        case 'amount': {
          this.calculateNetAmountFromAmount(item, taxCode, currency.digits);
          this.calculateUnitPrice(item, taxCode, currency.price_digits);
          break;
        }
      }
    } finally {
      this._recalculating = false;
    }
  }
}
